import { AcAdd, Button, Heading, IconButton, NaClose, Progress } from '@metservicedev/met-ui';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { HorizontalCollapse } from '../components/HorizontalCollapse';
import { PortalInfo } from '../components/PortalInfo';
import { Scrim } from '../components/Scrim';
import { Vona } from '../components/Vona';
import { useCurrentVonas } from '../hooks/useCurrentVonas';
import { createStyles } from '../tss';
import { CreateVona } from './CreateVona';

const useStyles = createStyles(({ theme }) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 34,
    minHeight: 'calc(100vh - 44px)',
  },
  subheader: {
    display: 'flex',
    justifyContent: 'flex-end',
    background: theme.color.bg.brand0,
    padding: `${theme.spacing(1)}px ${theme.spacing(8)}px`,
    [theme.breakpoints.below('sm')]: {
      padding: theme.spacing(1),
    },
  },
  loading: {
    width: 600,
    margin: '0 auto',
    marginTop: 100,
  },
  vonas: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: `${theme.spacing(4)}px ${theme.spacing(8)}px`,
    paddingBottom: 0,
    [theme.breakpoints.below('md')]: {
      padding: theme.spacing(2),
      paddingBottom: 0,
      gap: theme.spacing(2),
    },
    [theme.breakpoints.below('sm')]: {
      padding: theme.spacing(1),
      paddingBottom: 0,
      gap: theme.spacing(1),
    },
  },
  scrim: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: '100vw',
  },
  editDrawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100vh',
    backgroundColor: theme.color.container.default,
    borderRadius: theme.spacing(1),
    boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    maxWidth: '100vw',
    zIndex: 1,
  },
  editDrawerContainer: {
    padding: theme.spacing(2),
    position: 'relative',
    maxWidth: '100vw',
  },
  editHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.color.border.default}`,
  },
  editForm: {
    height: '100%',
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    marginRight: -theme.spacing(1.5),
  },
  form: {
    marginTop: theme.spacing(2),
  },
  footer: {
    position: 'absolute',
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    opacity: 0.5,
  },
}));

interface Props {
  edit?: boolean;
}

export const ListVona: React.FC<Props> = ({ edit }) => {
  const navigate = useNavigate();
  const { classes, theme } = useStyles();
  const { vonas, loading } = useCurrentVonas();
  const [expandedNumber, setExpandedNumber] = React.useState<string | null>(null);
  const sortedVonas = vonas.sort((a, b) => {
    return a.volcano.name.localeCompare(b.volcano.name);
  });

  return (
    <div className={classes.root}>
      <div className={classes.subheader}>
        <Button
          data-testid="issue-new-vona-button"
          variant="secondary"
          onClick={() => navigate('/edit')}
        >
          Issue New Vona <AcAdd />
        </Button>
      </div>
      <div className={classes.vonas}>
        {loading && (
          <div className={classes.loading} data-testid="loading-animation">
            <Progress size="xl" />
          </div>
        )}
        {!loading && sortedVonas.map((vona) => (
          <Vona
            key={vona.volcano.number}
            expanded={expandedNumber === vona.volcano.number}
            onClick={() => {
              if (expandedNumber === vona.volcano.number) {
                setExpandedNumber(null);
              } else {
                setExpandedNumber(vona.volcano.number);
              }
            }}
            vona={vona}
          />
        ))}
      </div>
      <div className={classes.footer}>
        <PortalInfo />
      </div>
      <Scrim className={classes.scrim} show={!!edit}>
        <HorizontalCollapse
          classes={{
            root: classes.editDrawer,
            container: classes.editDrawerContainer,
          }}
          open={!!edit}
          width={theme.breakpoints.sm}
          unmountOnExit
        >
          <div className={classes.editHeader}>
            <Heading size="lg" as="span">Edit VONA</Heading>
            <IconButton
              data-testid="close-edit-drawer-button"
              icon={NaClose}
              onClick={() => navigate('/')}
              variant="tertiary"
            />
          </div>
          <div className={classes.editForm}>
            <CreateVona className={classes.form} />
          </div>
        </HorizontalCollapse>
      </Scrim>
    </div>
  );
};
